import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../hooks';
import { ReactComponent as StatusBad } from '../../../img/icons/order-status-bad.svg';
import { ReactComponent as StatusNa } from '../../../img/icons/order-status-na.svg';
import { ReactComponent as StatusOk } from '../../../img/icons/order-status-ok.svg';
import { ReactComponent as StatusWarn } from '../../../img/icons/order-status-wait.svg';
import NoCover from '../../../img/no-cover.svg';
import { Contract, ContractStatus, useLazyGetContractQuery } from '../../../services/content_v1';
import { formatBytes, toMplatDate, toMplatValidity, useIsConsumer, useIsProvider } from '../../common';
import Button from '../Button/Button';
import { CreateContractModal } from '../Deals/RegistryContent';
import ImageLoader from '../ImageLoader';
import { Modal } from '../Modal';

interface ContractModalProps {
    isOpen: boolean,
    contractId: number,
    onClose: () => void,
}

function ContractModal({ isOpen, contractId, onClose }: ContractModalProps) {
    const auth = useAuth();
    const isConsumer = useIsConsumer();
    const isProvider = useIsProvider();

    const [fetchContract, { data: contractData }] = useLazyGetContractQuery();

    const [contract, setContract] = useState<Contract | undefined>(undefined);
    const [showCreateContractDialog, setShowCreateContractDialog] = useState(false);

    const toStatus = (s: ContractStatus) => {
        switch (s) {
            case ContractStatus.CONCLUDED:
                return <><StatusOk /><Trans>order:CONTRACT_CONCLUDED</Trans></>
            case ContractStatus.AGREEMENT:
                return <><StatusWarn /><Trans>order:CONTRACT_AGREEMENT</Trans></>
            case ContractStatus.TERMINATED:
                return <><StatusBad /><Trans>order:CONTRACT_TERMINATED</Trans></>
            default:
                return <><StatusNa /><Trans></Trans></>
        }
    }

    const getHead = (c: Contract) => {
        return isProvider ? (
            <>
                <Trans>Contract with</Trans> <Link to={`/accounts/view/${c.consumer.id}`}>{c.consumer.org?.name || c.consumer.name}</Link>
            </>
        ) : (
            isConsumer ?
                <>
                    <Trans>Contract with</Trans> <Link to={`/accounts/view/${c.provider.id}`}>{c.provider.org?.name || c.provider.name}</Link>
                </>
                :
                <>
                    <Trans>Contract between</Trans>
                    <Link to={`/accounts/view/${c.provider.id}`}>{c.provider.org?.name || c.provider.name}</Link>
                    <Trans>and</Trans>
                    <Link to={`/accounts/view/${c?.consumer.id}`}>{c.consumer.org?.name || c.consumer.name}</Link>
                </>
        )
    }

    useEffect(() => {
        if (!isNaN(contractId)) {
            fetchContract(contractId);
        }
    }, [auth, contractId]);

    useEffect(() => {
        if (contractData?.results) {
            setContract(contractData?.results);
        }
    }, [contractData]);

    return (
        <Modal className={'order__permission-popup'} isOpen={isOpen} onClose={() => onClose()}>
            <CreateContractModal isOpen={showCreateContractDialog} onClose={() => setShowCreateContractDialog(false)} contractId={contract?.id} />

            {contract && (
                <div className="order__permission">
                    <div className="user-settings__labels">
                        <span className="user-settings__labels-item">ID · {contractId}</span>
                        <span className="user-settings__labels-item">{toMplatDate(contract.validity?.lower, true)}</span>
                    </div>
                    <h2 className="popup__heading">
                        {getHead(contract)}
                    </h2>

                    <h3><Trans>Provider</Trans></h3>
                    <p>{contract.provider.org?.name || contract.provider.name}</p>
                    <h3><Trans>Consumer</Trans></h3>
                    <p>{contract.consumer.org?.name || contract.consumer.name}</p>
                    <h3><Trans>Contract number</Trans></h3>
                    <p>{contract.number}</p>
                    <h3><Trans>Contract term</Trans></h3>
                    <p>{toMplatValidity(contract.validity)}</p>

                    <h3><Trans>Content</Trans></h3>
                    <div className="tv-content__order-list">
                        {contract.content?.map((unit) => <div key={unit.id} className="tv-content__order-list-item">
                            {unit.covers.length > 0 ? <ImageLoader url={unit.covers[0]} /> : <img src={NoCover} alt="" />}
                            <span className="tv-content__order-list-item-name">{unit.title}</span>
                            <span className="tv-content__order-list-item-theme">{unit.category?.name}</span>
                        </div>)}
                    </div>

                    <h3><Trans>Status</Trans></h3>
                    <p>
                        <span className="order__status-label">
                            {toStatus(contract.status)}
                            <img src="img/icons/order-status-ok.svg" alt="" />
                        </span>
                    </p>

                    <h3><Trans>Files</Trans></h3>
                    <div className="order__files">
                        {contract.files.map((f) => <div key={f.id} className="order__files-item">
                            <div className="order__files-item-name">{f.name}</div>
                            <div className="order__files-item-size">{formatBytes(f.size)}</div>
                        </div>)}
                    </div>

                    <div className="order__permission-cta">
                        {contract.permits.length === 0 ?
                            <div className="order__permission-cta-item">
                                <Button onClick={() => {
                                    setShowCreateContractDialog(true);
                                }}>
                                    <Trans>Redact</Trans>
                                </Button>
                            </div>
                            :
                            <div className="order__permission-cta-item">
                                <Button onClick={onClose}>
                                    <Trans>Close</Trans>
                                </Button>
                            </div>
                        }
                    </div>
                </div>
            )}
        </Modal>
    )
}

export default ContractModal;